import React, { useState, useEffect } from "react";
import { Clock, Users, Tag, AlertCircle, MapPin } from "lucide-react";
import { fileUrl } from "../lib/api";

function getCountdown(date, cutoffTime, cutoffDate) {
  const cutDateStr = cutoffDate || date;
  const [hh, mm] = cutoffTime.split(":").map(Number);
  const [y, mo, d] = cutDateStr.split("-").map(Number);
  const cut = new Date(y, mo - 1, d, hh, mm, 0, 0);
  const diff = cut - new Date();
  if (diff <= 0) return { closed: true, label: "Chiuso" };
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return { closed: false, label: `${mins} min`, urgent: true };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return { closed: false, label: `${hrs}h ${mins % 60}m`, urgent: hrs < 1 };
  const days = Math.floor(hrs / 24);
  return { closed: false, label: `${days}g ${hrs % 24}h`, urgent: false };
}

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1632778129004-f142ce499b3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxpdGFsaWFuJTIwcGFzdGElMjBkaXNofGVufDB8fHx8MTc3ODY1NjA3OHww&ixlib=rb-4.1.0&q=85";

export default function DishCard({ dish, onBook, footer, hideAction }) {
  const [countdown, setCountdown] = useState(getCountdown(dish.date, dish.cutoff_time, dish.cutoff_date));

  useEffect(() => {
    const t = setInterval(() => setCountdown(getCountdown(dish.date, dish.cutoff_time, dish.cutoff_date)), 30000);
    return () => clearInterval(t);
  }, [dish.date, dish.cutoff_time, dish.cutoff_date]);

  const photoSrc = dish.photo_path ? fileUrl(dish.photo_path) : FALLBACK_IMG;
  const soldOut = dish.portions_available <= 0;
  const isClosed = countdown?.closed;
  const canBook = !soldOut && !isClosed;

  return (
    <article
      className="bg-card border border-border rounded-lg overflow-hidden transition-all duration-200 hover:border-foreground/15"
      data-testid={`dish-card-${dish.id}`}
    >
      <div className="aspect-[16/10] bg-muted relative overflow-hidden">
        <img
          src={photoSrc}
          alt={dish.name}
          className="w-full h-full object-cover"
          onError={(e) => (e.target.src = FALLBACK_IMG)}
        />
        {countdown && (
          <span
            className={`absolute top-3 right-3 px-2.5 py-1 rounded-md text-xs font-medium ${
              isClosed
                ? "bg-foreground/80 text-white"
                : countdown.urgent
                ? "bg-accent text-white"
                : "bg-white/95 text-foreground"
            }`}
          >
            <Clock className="w-3 h-3 inline mr-1" />
            {countdown.label}
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-display text-lg font-semibold leading-tight">{dish.name}</h3>
          <span className="font-display font-semibold text-lg text-primary whitespace-nowrap">
            € {Number(dish.price).toFixed(2)}
          </span>
        </div>
        {dish.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{dish.description}</p>
        )}
        {dish.ingredients && (
          <div className="text-xs text-muted-foreground mb-2 flex items-start gap-1.5">
            <Tag className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{dish.ingredients}</span>
          </div>
        )}
        {dish.allergens && (
          <div className="text-xs text-accent mb-3 flex items-start gap-1.5">
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Allergeni: {dish.allergens}</span>
          </div>
        )}
        {dish.pickup_location && (
          <div className="text-xs text-muted-foreground mb-2 flex items-start gap-1.5" data-testid={`dish-pickup-${dish.id}`}>
            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-primary" />
            <span><span className="font-medium text-foreground">Ritiro:</span> {dish.pickup_location}</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-1.5 text-sm">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span
              className={`font-medium ${soldOut ? "text-accent" : "text-foreground"}`}
              data-testid={`dish-portions-${dish.id}`}
            >
              {dish.portions_available} / {dish.portions_total}
            </span>
            <span className="text-muted-foreground">disp.</span>
          </div>
          {!hideAction &&
            (canBook ? (
              <button
                onClick={() => onBook?.(dish)}
                data-testid={`book-button-${dish.id}`}
                className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-[#436343] active:scale-95 transition-all duration-200"
              >
                Prenota
              </button>
            ) : (
              <span className="text-xs text-muted-foreground italic">
                {soldOut ? "Esaurito" : "Chiuso"}
              </span>
            ))}
        </div>
        {footer}
      </div>
    </article>
  );
}
